import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
import { TOAST_EVENT, ToastInput, ToastType } from '../lib/toastBus';

interface Toast extends ToastInput {
  id: number;
}

interface ToastApi {
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
}

interface ToastContextValue {
  toast: ToastApi;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const DEFAULT_DURATION = 5000;

const ICONS: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle className="w-5 h-5 text-green-500" aria-hidden="true" />,
  error: <AlertCircle className="w-5 h-5 text-red-500" aria-hidden="true" />,
  info: <Info className="w-5 h-5 text-primary-500" aria-hidden="true" />,
};

const TONE: Record<ToastType, string> = {
  success: 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20',
  error: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20',
  info: 'border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20',
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);
  const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const handle = timers.current[id];
    if (handle) {
      clearTimeout(handle);
      delete timers.current[id];
    }
  }, []);

  const push = useCallback(
    (input: ToastInput) => {
      idRef.current += 1;
      const id = idRef.current;
      const duration = input.duration ?? DEFAULT_DURATION;
      setToasts((prev) => [...prev, { ...input, id }]);
      if (duration > 0) {
        timers.current[id] = setTimeout(() => dismiss(id), duration);
      }
    },
    [dismiss],
  );

  // Bridge: let non-React code (utils, the API layer) raise toasts.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ToastInput>).detail;
      if (detail && detail.message) push(detail);
    };
    window.addEventListener(TOAST_EVENT, handler);
    return () => window.removeEventListener(TOAST_EVENT, handler);
  }, [push]);

  // Clear any pending timers on unmount.
  useEffect(() => {
    const store = timers.current;
    return () => {
      Object.values(store).forEach((handle) => clearTimeout(handle));
    };
  }, []);

  const toast: ToastApi = {
    success: (message, duration) => push({ type: 'success', message, duration }),
    error: (message, duration) => push({ type: 'error', message, duration }),
    info: (message, duration) => push({ type: 'info', message, duration }),
  };

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      <div
        className="fixed top-4 right-4 z-[100] flex w-[min(92vw,24rem)] flex-col gap-2 pointer-events-none"
        role="region"
        aria-label="Notifications"
      >
        <AnimatePresence initial={false}>
          {toasts.map((t) => (
            <motion.div
              key={t.id}
              layout
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 40 }}
              role={t.type === 'error' ? 'alert' : 'status'}
              className={`pointer-events-auto flex items-start gap-3 rounded-xl border p-4 shadow-lg ${TONE[t.type]}`}
            >
              <span className="mt-0.5 flex-shrink-0">{ICONS[t.type]}</span>
              <p className="flex-1 break-words text-sm text-gray-800 dark:text-slate-100">{t.message}</p>
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss notification"
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
              >
                <X className="h-4 w-4" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
};
